/**
 * In-app help — a single static walkthrough of the research-run flow (P22).
 *
 * One section per screen, plain language, no marketing. Terms are kept
 * consistent with the operator manual (docs/operator-manual.md): "workspace"
 * never "tenant", and "Scout"/"Tali" never appear in copy — the module is
 * "Research Runs" and the output is "Leads" (routes stay /scout/*).
 *
 * Linked from the sidebar bottom nav (components/layout/Sidebar.tsx). Static
 * content only — no data fetching, no gate: any signed-in user can read it.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { PageHeader } from '@/components/layout/PageHeader';

export const Route = createFileRoute('/help')({
  component: HelpPage,
});

interface StepProps {
  n: number;
  title: string;
  to?: string;
  toLabel?: string;
  children: React.ReactNode;
}

/** One screen in the walkthrough: a numbered card with an optional jump link. */
function Step({ n, title, to, toLabel, children }: StepProps) {
  return (
    <section className="card" aria-labelledby={`help-step-${n}`}>
      <div className="flex items-start gap-3">
        <span
          className="flex items-center justify-center h-6 w-6 shrink-0 rounded-full bg-secondary/10 text-secondary text-xs font-medium tabular-nums"
          aria-hidden="true"
        >
          {n}
        </span>
        <div className="min-w-0 space-y-2">
          <h2 id={`help-step-${n}`} className="text-base font-medium text-text">
            {title}
          </h2>
          <div className="text-sm text-muted space-y-2">{children}</div>
          {to && (
            <Link to={to} className="inline-block text-sm text-primary hover:underline">
              {toLabel ?? 'Open'} →
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/** One term in the reference list. */
function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <dt className="text-sm font-medium text-text">{label}</dt>
      <dd className="text-sm text-muted mt-1">{children}</dd>
    </div>
  );
}

function HelpPage() {
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader title="Help" />

      <p className="text-sm text-muted">
        A research run turns raw company and job data into a reviewed list of qualified leads. You compose
        the criteria for a run, launch it, watch it work, review the results at a single quality
        gate, and export the leads you approve as a CSV. This page walks through each screen in
        that order.
      </p>

      <h2 className="text-xs font-medium uppercase tracking-wider text-muted pt-2">
        The flow, screen by screen
      </h2>

      <Step n={1} title="Compose your criteria" to="/scout/compose" toLabel="Criteria Composer">
        <p>
          Every run starts from criteria you build, not a fixed search. The composer stacks three
          layers:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="text-text">Always-on workspace criteria</span> - your company identity
            plus the lists that are never contacted (current clients, competitors, confirmed
            bad-fit). These default into every run for the workspace.
          </li>
          <li>
            <span className="text-text">A class template</span> - a reusable, saved set of roles and
            settings for one kind of search. Pick one and adjust it.
          </li>
          <li>
            <span className="text-text">This-run specifics</span> - the size band, geography, roles,
            and keywords for today's run.
          </li>
        </ul>
        <p>
          Each role is matched either <span className="text-text">exact</span> (that title only) or{' '}
          <span className="text-text">loose</span>. A loose role opens a synonym step: the composer proposes
          equivalent titles and you confirm, edit, or add before continuing.
        </p>
        <p>
          Before you can launch, you&apos;ll see the full composed criteria - every layer, the confirmed
          synonyms, and the generated boolean - exactly as it will be recorded with the run. This
          preview is the only place a run can be launched.
        </p>
      </Step>

      <Step n={2} title="Launch the run">
        <p>
          You launch from the preview. The run is recorded together with the exact criteria version
          it used, so any batch can always be traced back to the criteria that produced it. Each
          launch counts against your workspace's monthly run cap (see below).
        </p>
      </Step>

      <Step n={3} title="Watch it run" to="/scout/runs" toLabel="Runs">
        <p>
          Every run has a status and a stage scorecard - a per-stage count of what went in, what
          came out, and what was set aside at each step of the pipeline. The page updates while the
          run is still working, and the scorecard shows exactly where a batch narrowed.
        </p>
      </Step>

      <Step n={4} title="Review at the QA gate" to="/scout/runs" toLabel="Runs">
        <p>
          Every lead is graded A, B, C, or D with a reason, quoted evidence, and a confidence
          score - but nothing is final until you review it. This is the single human checkpoint.
          Open a completed run's review to step through each lead: accept the grade it was given,
          or override it, and reject leads with a reason.
        </p>
        <p>
          Rejections for deterministic reasons (a competitor, the wrong role, a stale job) become
          suppression rules that filter future runs automatically.
        </p>
      </Step>

      <Step n={5} title="Export a CSV" to="/scout/export" toLabel="Export">
        <p>
          Pick a completed, reviewed run and choose the scope. <span className="text-text">A+B
          (usable)</span> is the sendable list. <span className="text-text">A+B+C+D (review)</span>{' '}
          is a separate file with its own name, so a review export can never be mistaken for the
          sendable one.
        </p>
        <p>
          Preview the rows, then generate and download. The CSV carries the full evidence trail for
          each lead. Re-downloading a run produces the same file.
        </p>
      </Step>

      <Step n={6} title="Keep suppression lists current" to="/scout/suppressions" toLabel="Suppressions">
        <p>
          Your workspace's always-on never-contact lists live here - current clients, competitors,
          and confirmed bad-fit. Add entries by pasting in bulk or by type. Competitor entries are
          reviewed before they go active; the rest apply immediately. Everything on these lists
          defaults into every run.
        </p>
      </Step>

      <h2 className="text-xs font-medium uppercase tracking-wider text-muted pt-2">
        Terms you'll see
      </h2>

      <section className="card" aria-label="Glossary">
        <dl className="space-y-3">
          <Term label="A / B / C / D grades">
            The quality grade given to each lead. <span className="text-text">A and B are usable</span>
            {' '}- good enough to send - and make up the default export. C and D are for review only
            and are not part of the sendable list. Every grade comes with a reason and quoted
            evidence, and you can override it at the QA gate.
          </Term>
          <Term label="Catchall flag (on export)">
            Some domains accept mail at any address, so no individual mailbox can be verified.
            Those rows are marked with a catchall flag rather than dropped: a real person may be
            behind the address, but delivery isn't guaranteed. It's a warning, not a rejection -
            decide before you send.
          </Term>
          <Term label="Held leads">
            A lead set aside because a required field was missing (company name, website, job
            title, or job URL). Rather than being discarded, it is held for review so nothing is lost
            silently. Held leads aren't exported; they can be recalled into a later run once the
            missing data is available.
          </Term>
          <Term label="Run caps">
            Each workspace has a limit on runs per month and on contact lookups per run - reach the
            monthly limit and new launches wait until the next month, while any leads past the
            per-run lookup limit are kept aside (marked capped), never dropped.
          </Term>
        </dl>
      </section>
    </div>
  );
}
